import React, { useId } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, Clock, List, LockKeyhole } from 'lucide-react';
import { Badge } from 'components/ui/badge';
import { Button } from 'components/ui/button';
import { formatGroupedNumber } from 'lib/dateFormat';
import { cn } from 'lib/utils';
import type { PublicTestCardItem } from 'modules/test/hooks/usePublicTests';
import type { TestHolding } from 'modules/test/hooks/useMyTestHoldings';
import { publicQuestionsCount } from 'modules/test/public-question-count';

/** Marketplace levels, in the order the level filter lists them. */
export const LEVELS = ['beginner', 'intermediate', 'advanced', 'exam_standard'] as const;
export type Level = (typeof LEVELS)[number];

export const LEVEL_LABEL_KEYS: Record<Level, string> = {
  beginner: 'portal_tests_level_beginner',
  intermediate: 'portal_tests_level_intermediate',
  advanced: 'portal_tests_level_advanced',
  exam_standard: 'portal_tests_level_exam_standard',
};

/** The API reports a test without a level as the exam standard. */
function levelKey(level?: string): string {
  const key = (level ?? '').toLowerCase() as Level;
  return LEVEL_LABEL_KEYS[key] ?? LEVEL_LABEL_KEYS.exam_standard;
}

interface PublicTestCardProps {
  test: PublicTestCardItem;
  /** The category's label; the raw slug is never shown. */
  categoryLabel?: string;
  /** A plan test the candidate cannot open right now, with the reason under the meta. */
  lockedReason?: string;
  /** The learner already has this test open or paid for: no price, no charge on the way in. */
  holding?: TestHolding;
  disabled?: boolean;
  onAction: () => void;
}

/**
 * One catalog card: category and level, the title in Plus Jakarta, two lines of
 * description, duration and question count, then the price and one ink action.
 * The action's hit area covers the card, so the whole card opens the test.
 */
export function PublicTestCard({ test, categoryLabel, lockedReason, holding, disabled, onAction }: PublicTestCardProps) {
  const { t } = useTranslation();
  // Every card's action reads the same ("Sotib olish"); the title tells them apart for screen readers.
  const titleId = useId();
  const locked = Boolean(lockedReason);
  const questionsCount = publicQuestionsCount(test);
  const inProgress = holding === 'IN_PROGRESS';
  // A begun free test keeps its short "Free"; any other price gives the whole row to the long resume action.
  const showPrice = !inProgress || test.accessType === 'FREE';

  const price =
    holding === 'PURCHASED' ? (
      <span className="inline-flex items-center gap-1.5 text-[13px] text-foreground/80">
        <Check size={14} strokeWidth={2.5} className="shrink-0 text-success-600" aria-hidden="true" />
        {t('portal_tests_paid')}
      </span>
    ) : test.accessType === 'FREE' ? (
      <span className="text-sm font-semibold text-success-700 dark:text-success-400">{t('free')}</span>
    ) : test.accessType === 'PAID' ? (
      <span className="text-[13px] text-muted-foreground">
        <span className="text-base font-bold tabular-nums text-foreground">{formatGroupedNumber(test.priceSom ?? 0)}</span>{' '}
        {t('portal_tests_currency')}
      </span>
    ) : locked ? (
      <span className="inline-flex items-center gap-1.5 text-[13px] text-foreground/80">
        <LockKeyhole size={14} className="shrink-0 text-muted-foreground" aria-hidden="true" />
        {t('portal.plan_required')}
      </span>
    ) : (
      <span className="inline-flex items-center gap-1.5 text-[13px] text-foreground/80">
        <Check size={14} strokeWidth={2.5} className="shrink-0 text-success-600" aria-hidden="true" />
        {t('portal.plan_included')}
      </span>
    );

  const action =
    inProgress
      ? t('portal_tests_resume')
      : test.accessType === 'FREE' || holding === 'PURCHASED'
        ? t('portal_tests_take')
        : test.accessType === 'PAID'
          ? t('portal_tests_buy')
          : t('portal.unlock_test');

  return (
    <article className="relative flex min-h-[244px] flex-col overflow-hidden rounded-lg border border-border bg-card transition-[border-color,box-shadow] duration-150 hover:border-foreground/20 hover:shadow-xs">
      {/* Its own band, not a footer label beside the long resume action, which squeezed both. */}
      {inProgress && (
        <div className="flex items-center gap-2 border-b border-primary-600/30 bg-primary/25 px-5 py-2 text-[13px] font-semibold text-foreground">
          <span aria-hidden="true" className="h-2 w-2 shrink-0 rounded-full bg-foreground motion-safe:animate-pulse" />
          <span className="min-w-0 truncate">{t('in_progress')}</span>
        </div>
      )}
      <div className="flex flex-1 flex-col p-5">
        <div className="flex items-center justify-between gap-2">
          {categoryLabel ? <Badge className="h-[22px] py-0">{categoryLabel}</Badge> : <span />}
          <span className="truncate text-[12.5px] text-muted-foreground">{t(levelKey(test.difficultyLevel))}</span>
        </div>
        <h3 id={titleId} className="mt-3.5 line-clamp-2 font-display text-[17px] font-bold leading-[1.3] tracking-[-0.01em] text-foreground">
          {test.title}
        </h3>
        {test.description && (
          <p className="mt-1.5 line-clamp-2 text-[13.5px] leading-normal text-muted-foreground">{test.description}</p>
        )}
        <div className="mt-auto flex flex-wrap gap-x-4 gap-y-1 pt-3.5 text-[13px] text-foreground/80">
          <span className="inline-flex items-center gap-1.5">
            <Clock size={15} className="shrink-0 text-muted-foreground/70" aria-hidden="true" />
            <span className="tabular-nums">{test.durationMinutes}</span> {t('minutes')}
          </span>
          {questionsCount !== undefined && (
            <span className="inline-flex items-center gap-1.5">
              <List size={15} className="shrink-0 text-muted-foreground/70" aria-hidden="true" />
              <span className="tabular-nums">{questionsCount}</span> {t('pager_noun_questions')}
            </span>
          )}
        </div>
        {lockedReason && <p className="mt-3 text-xs text-muted-foreground">{lockedReason}</p>}
        <div className="mt-3.5 flex items-center justify-between gap-2.5 border-t border-border pt-3.5">
          {showPrice && price}
          <Button
            variant="ink"
            size="sm"
            disabled={disabled}
            onClick={onAction}
            aria-describedby={titleId}
            className={cn(
              "px-3.5 text-[13.5px] after:absolute after:inset-0 after:content-['']",
              showPrice ? 'shrink-0' : 'w-full',
            )}
          >
            {action}
          </Button>
        </div>
      </div>
    </article>
  );
}

export default PublicTestCard;
